import React, { useRef, useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import * as yup from "yup";
import { MCheckboxGroup } from "components/Shared";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MCard from "components/Shared/MCard.tsx";
import {
  DATA_TYPE_DROPDOWN2,
  DATA_TYPE_ADDRESS_AUTOCOMPLETE,
  DATA_TYPE_ADDRESS,
  DATA_TYPE_NUMBER_STRING,
  DATA_TYPE_NUMBER,
  DATA_TYPE_NUMBER_UNFORMATTED,
  DATA_TYPE_STRING,
} from "typeConstants/dataTypes";
import OrderStepButtons from "components/WebOrderV2/OrderStepButtons";
import { LENDER } from "typeConstants/webOrder";
import { nanToNull } from "tools/validation";
import { getZipCodes } from "actions/assignment";


const MultiProperty = (props) => {

  const [inspections, setInspections] = useState([]);

  const mCardControlMethodsProperty = useRef({});
  const checkboxControlMethods = useRef({});

  useEffect(() => {
    props.getZipCodes();
    if (props.data.inspections?.length) {
      setInspections(props.data.inspections.slice(0));
    }
  }, []);

  const setControlMethodsProperty = () => {
    return cm => (mCardControlMethodsProperty.current = cm);
  };

  const onSurveyAddressSearch = (address) => {
    mCardControlMethodsProperty.current.setEditData("address1", address.streetAddress);
    mCardControlMethodsProperty.current.setEditData("address2", "");
    mCardControlMethodsProperty.current.setEditData("city", address.city);
    mCardControlMethodsProperty.current.setEditData("state", { label: address.state, value: address.state });
    mCardControlMethodsProperty.current.setEditData("zipCode", address.zip);
    mCardControlMethodsProperty.current.setEditData("yearBuilt", "");
    mCardControlMethodsProperty.current.setEditData("sqFt", "");
    mCardControlMethodsProperty.current.setEditData("constructionType", "");
  };

  const getPropertyFields = () => {
    return [
      {
        key: "searchSurveyAddress",
        dataType: DATA_TYPE_ADDRESS_AUTOCOMPLETE,
        label: "Address Search",
        onChange: onSurveyAddressSearch,
        value: "",
      },
      {
        key: "address1",
        dataType: DATA_TYPE_ADDRESS,
        label: "Address 1",
        value: "",
        validation: yup.string().max(75, "Address cannot exceed 75 characters").required("Address 1 is required"),
        placeholder: "Required",
      },
      {
        key: "address2",
        dataType: DATA_TYPE_STRING,
        label: "Address 2",
        validation: yup.string().max(75, "Address 2 cannot exceed 75 characters"),
        value: "",
      },
      {
        key: "city",
        label: "City",
        value: "",
        validation: yup.string().max(50, "City cannot exceed 50 characters").required("City is required"),
        placeholder: "Required",
      },
      {
        key: "state",
        dataType: DATA_TYPE_DROPDOWN2,
        label: "State",
        value: "",
        dataTypeOptions: { options: props.stateOptions },
        validation: yup.string().required("State is required"),
        placeholder: "Select... (required)",
      },
      {
        key: "zipCode",
        dataType: DATA_TYPE_NUMBER_STRING,
        label: "Zip",
        value: "",
        validation: yup.string().min(5, "Value must be 5 digits").max(5, "Value must be 5 digits").test(
          "is-valid-zip",
          (z) => `${z.value} is not a valid zip code for the entered state`,
          (z) => {
            //Since the field data is tossed when going to the previous page we only have to check the edited data object, unlike single property where we check the props.data if there's no edited data
            let stateToValidate = mCardControlMethodsProperty.current.getEditedDataObject().state;
            return props.zipCodes.find((zip) => z === zip.zipCode && stateToValidate === zip.stateAbbreviation);
          }
        ).required("Zip is required"),
        placeholder: "Required",
      },
      {
        key: "yearBuilt",
        dataType: DATA_TYPE_NUMBER_UNFORMATTED,
        label: "Year Built",
        value: "",
        validation: yup.number().integer("Value must be a whole number").min(1701, "Value must be greater than 1700").max(new Date().getFullYear(), "Value must not be more than the current year").transform(nanToNull).nullable()
      },
      {
        key: "sqFt",
        dataType: DATA_TYPE_NUMBER,
        label: "Square Ft.",
        value: "",
        validation: yup.number().integer("Value must be a whole number").min(1, "Value must be greater than zero").transform(nanToNull).nullable()
      },
      {
        key: "constructionType",
        dataType: DATA_TYPE_DROPDOWN2,
        label: "Construction Type",
        value: "",
        dataTypeOptions: { options: [
          { label: "", value: "" },
          { label: "Fire Resistive", value: "Fire Resistive" },
          { label: "Modified Fire Resistive", value: "Modified Fire Resistive" },
          { label: "Frame", value: "Frame" },
          { label: "Heavy Timber", value: "Heavy Timber" },
          { label: "Joisted Masonry", value: "Joisted Masonry" },
          { label: "Noncombustible", value: "Noncombustible" },
          { label: "Masonry Noncombustible", value: "Masonry Noncombustible" },
        ] },
        placeholder: "Select...",
      },
    ];
  };

  const memoizedSupplementCheckboxes = useMemo(
    () => {
      return props.availableSupplements.map(s => {
        let isSelected = props.data.supplements?.includes(s.code);
        return {
          label: s.name,
          value: s.code,
          defaultChecked: isSelected
        };
      });
    },
    [props.availableSupplements]
  );

  const getPropertiesToInspect = () => {
    return inspections.map((insp, index) => {
      let propertyString = `${insp.address1}\n${(insp.address2 && insp.address2.length > 0 ? insp.address2 + "\n" : "")}${insp.city}, ${insp.state} ${insp.zipCode}`;

      if (insp.yearBuilt)
        propertyString += `\nYear Built: ${insp.yearBuilt}`;

      if (insp.sqFt)
        propertyString += `\nSq Ft: ${insp.sqFt}`;

      if (insp.constructionType)
        propertyString += `\nConstruction Type: ${insp.constructionType}`;

      const supplementString = insp.supplements.reduce((acc, current) => {
        const label = props.availableSupplements.filter(s => s.code === current)[0].name;
        return acc + `${label}\n`;
      }, "");

      return (
        <TableRow key={`row-${index}`}>
          <TableCell><pre>{propertyString}</pre></TableCell>
          <TableCell><pre>{supplementString}</pre></TableCell>
          <TableCell>
            <i className="md-icon material-icons" style={{ cursor: "pointer" }} onClick={() => { onRemoveClicked(index); }}>delete_forever</i>
          </TableCell>
        </TableRow>
      );
    });
  };

  const onAddClicked = () => {
    const isPropertyValid = mCardControlMethodsProperty.current.runValidation(true);
    if (!isPropertyValid) return;

    const propertyInfo = mCardControlMethodsProperty.current.getEditedDataObject();
    const inspection = {
      ...propertyInfo,
      supplements: props.availableSupplements.length > 0 ? checkboxControlMethods.current?.getChecked() : []
    };

    // check for duplicates
    for (const insp of inspections) {
      if (insp.address1 === inspection.address1
        && insp.address2 === inspection.address2
        && insp.zipCode === inspection.zipCode) {
        return;
      }
    }

    if (checkboxControlMethods && "unCheckAll" in checkboxControlMethods.current) {
      checkboxControlMethods.current.unCheckAll();
    }

    const nextInspections = inspections.slice(0);
    nextInspections.push(inspection);
    setInspections(nextInspections);
  };

  const onRemoveClicked = (index) => {
    const nextInspections = inspections.slice(0);
    nextInspections.splice(index, 1);
    setInspections(nextInspections);
  };

  const onPrevious = () => {
    props.onPrevious({ inspections: inspections });
  };
  const onNext = () => {
    if (inspections.length)
      props.onNext({ inspections: inspections });
  };

  let supplementsLabel = props.terminology === LENDER ? "Additional Forms" : "Supplements";

  return (
    <>
      <MCard
        title="Property to Add"
        getControlMethods={setControlMethodsProperty("property")}
        fields={getPropertyFields()}
        onlyEditable
      />
      <MCard
        title={supplementsLabel}
      >
        {props.availableSupplements.length > 0
          ? <MCheckboxGroup
            controlMethods={cm => checkboxControlMethods.current = cm}
            fields={memoizedSupplementCheckboxes}
          />
          : <p>{`No ${supplementsLabel} Allowed`}</p>}
      </MCard>
      <Button
        onClick={onAddClicked}
      >Add</Button>
      <MCard
        title={"Properties to Inspect"}>
        {!inspections.length && <p>None Added</p>}
        {inspections.length > 0 &&
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Address</TableCell>
                  <TableCell>Supplements</TableCell>
                  <TableCell>&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getPropertiesToInspect()}
              </TableBody>
            </Table>
          </TableContainer>
        }
      </MCard>
      <OrderStepButtons
        currentStep={props.currentStep}
        onPrevious={onPrevious}
        onNext={onNext}
        nextIsDisabled={inspections.length === 0}
        nextTitle={`Next (${inspections.length})`}
      />
    </>
  );
};

MultiProperty.propTypes = {
  currentStep: PropTypes.number,  // Current step in order process, needed for OrderStepButtons
  data: PropTypes.object, // Data for MCard fields
  onNext: PropTypes.func, // Func to call when fields are valid to move to the next step
  onPrevious: PropTypes.func, // Func to call to move to previous step
  terminology: PropTypes.string, // Insurance or Lender, see typeConstants/webOrder.js
  pageLayout: PropTypes.string, // LenderAppraisal, LenderNonAppraisal, Insurance, Commercial: see typeConstants/webOrder.js
  handlingFeatures: PropTypes.object,  // caseHandlingFeatures from settings
  stateOptions: PropTypes.array,  // State abbrevation options for address field
  availableSupplements: PropTypes.array,
};

const mapStateToProps = state => {
  return {
    zipCodes: state.assignment.zipCodes,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    getZipCodes(stateCodeList) {
      dispatch(getZipCodes(stateCodeList));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MultiProperty);